import axiosInstant from 'api/baseRequest'
import type { AxiosInstance } from 'axios'
import { parsePresentationType } from './qrLookupPresentation'
import {
  customerFromReservation,
  isConfirmRejected,
  isLookupRejected,
  mapQrLookupToDetail,
  QrLookupPresentationType,
  type QrConfirmRejectedPayload,
  type QrConfirmReservationData,
  type QrConfirmResult,
  type QrLookupRejectedPayload,
  type QrLookupReservationData,
  type QrLookupResult,
  type QrLookupWarningItem,
} from './types'

type QrLookupResponseBody = {
  success: boolean
  data?: QrLookupReservationData | QrLookupRejectedPayload
  message?: string
}

function findPresentationWarning(
  warnings: QrLookupWarningItem[] | undefined
): QrLookupWarningItem | undefined {
  if (!warnings?.length) return undefined
  return warnings.find(
    (w) =>
      w.type === QrLookupPresentationType.Warning ||
      w.type === 'warning'
  )
}

function mapRejectedPayload(
  payload: QrLookupRejectedPayload | QrConfirmRejectedPayload
): QrLookupResult {
  return {
    presentationType: parsePresentationType(payload.type),
    reason: payload.reason,
    title: payload.title ?? '',
    subtitle: payload.subtitle ?? '',
    message: (payload.message ?? '').trim(),
    customer: payload.customer,
    checkedInAt:
      payload.customer?.checkedInAt ??
      ('checkedInAt' in payload ? payload.checkedInAt : undefined),
    guestCount: payload.customer?.guestCount ?? payload.guestCount,
    showCheckIn: false,
  }
}

function mapLookupPayload(
  payload: QrLookupReservationData | QrLookupRejectedPayload
): QrLookupResult {
  if (isLookupRejected(payload)) {
    return mapRejectedPayload(payload)
  }

  const detail = mapQrLookupToDetail(payload)
  const customer = customerFromReservation(payload)
  const warning = findPresentationWarning(payload.warnings)

  if (warning) {
    return {
      presentationType: QrLookupPresentationType.Warning,
      reason: warning.code,
      title: warning.title ?? '',
      subtitle: warning.subtitle ?? '',
      message: (warning.message ?? '').trim(),
      detail,
      customer,
      guestCount: customer.guestCount ?? payload.guestCount,
      showCheckIn: true,
    }
  }

  return {
    presentationType: QrLookupPresentationType.Success,
    title: '',
    subtitle: '',
    message: '',
    detail,
    customer,
    guestCount: customer.guestCount ?? payload.guestCount,
    showCheckIn: true,
  }
}

export function createApiErrorLookupResult(
  message: string,
  fallbacks: { title: string; subtitle: string }
): QrLookupResult {
  return {
    presentationType: QrLookupPresentationType.Error,
    reason: 'api_error',
    title: fallbacks.title,
    subtitle: fallbacks.subtitle,
    message: message.trim(),
    showCheckIn: false,
  }
}

export async function lookupReservationByQrToken(
  params: {
    token: string
    eventId: string
  },
  client: AxiosInstance = axiosInstant
): Promise<QrLookupResult> {
  const { data } = await client.post<QrLookupResponseBody>(
    '/api/v1/reservation/qr/lookup',
    { token: params.token, eventId: params.eventId }
  )

  if (!data?.success || data.data == null) {
    const err = new Error(data?.message || 'Lookup failed') as Error & {
      response?: { data?: { message?: string } }
    }
    err.response = { data: { message: data?.message } }
    throw err
  }

  return mapLookupPayload(data.data)
}

type QrConfirmResponseBody = {
  success: boolean
  data?: QrConfirmReservationData | QrConfirmRejectedPayload
  message?: string
}

export async function confirmReservationQr(
  params: {
    token: string
    eventId: string
  },
  client: AxiosInstance = axiosInstant
): Promise<QrConfirmResult> {
  const { data } = await client.post<QrConfirmResponseBody>(
    '/api/v1/reservation/qr/confirm',
    { token: params.token.trim(), eventId: params.eventId }
  )

  if (!data?.success || data.data == null) {
    const err = new Error(data?.message || 'Confirm failed') as Error & {
      response?: { data?: { message?: string } }
    }
    err.response = { data: { message: data?.message } }
    throw err
  }

  const payload = data.data

  if (isConfirmRejected(payload)) {
    return { kind: 'rejected', result: mapRejectedPayload(payload) }
  }

  return { kind: 'ok', data: payload }
}
